import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { LockIcon, ShieldCheckIcon } from 'lucide-react';
import { Drawer } from '../shared/Drawer';
import { useCart, useOverlayActions, useOverlays } from '../../contexts/StoreContext';
import { founder } from '../../data/content';
import { policyCopy } from '../../data/policies';
import { GiftProgress } from './GiftProgress';
import { CartLine } from './CartLine';
import { CartBundle } from './CartBundle';
import { CartRhythm } from './CartRhythm';
import { groupCart } from '../../core/rules/cartBundles';
import { CartGifts } from './CartGifts';
import { CartCode, CartRecommendations } from './CartExtras';
import { CartAdjustedNotice } from './CartAdjustedNotice';
import { countSoldOut } from '../../core/rules/stock';
import { btnPrice, btnPriceAmount, btnPriceLabel, btnPrimary, btnSecondary } from '../../ui/styles';
import { euroCents } from './cartRules';
import { track } from '../../utils/analytics';

/** De winkelmand: producten in de hoofdrol, cadeaubalk erboven, totaal en afrekenen onderaan. */
export function CartDrawer() {
  const {
    cartOpen
  } = useOverlays();
  const {
    closeCart,
    openQuiz
  } = useOverlayActions();
  const {
    lines,
    subtotal,
    giftSubtotal,
    shipping,
    total,
    gifts,
    nextGift,
    giftTiers
  } = useCart();
  const navigate = useNavigate();
  const {
    pathname
  } = useLocation();
  const inAccount = pathname === '/account' || pathname.startsWith('/account/');
  const count = lines.reduce((n, l) => n + l.qty, 0);
  const soldOutCount = countSoldOut(lines.map(l => l.product));
  const footer = lines.length > 0 ? <div data-id="element-6899" data-mp="3jrypljyptfl1">
        <dl className="space-y-1.5 text-[14px] text-muted" data-id="element-6900" data-mp="3js8ipgf242uy">
          <div className="flex justify-between" data-id="element-6901" data-mp="3or1un4ep3lt0">
            <dt data-id="element-6902" data-mp="3otxsksqjmahg">Subtotaal</dt>
            <dd className="text-ink" data-id="element-6903" data-mp="28duwefzybvh1">{euroCents(subtotal)}</dd>
          </div>
          <div className="flex justify-between" data-id="element-6904" data-mp="28fxvdpj30yxw">
            <dt data-id="element-6905" data-mp="2ddqzmd0trivj">Verzending</dt>
            <dd className={shipping === 0 ? 'text-clay' : 'text-ink'} data-id="element-6906" data-mp="2ddzfmds8v4ay">{shipping === 0 ? 'Gratis' : euroCents(shipping)}</dd>
          </div>
        </dl>

        {/* Uitverkocht in de mand: hier al zeggen, niet pas bij afrekenen tegen een geblokkeerde knop aanlopen. */}
        {soldOutCount > 0 && <p className="mt-4 text-center text-[13px] text-clay" id="cart-blocked" data-id="element-6907" data-mp="a2ba34h3qg59">
            Haal eerst {soldOutCount === 1 ? 'het uitverkochte product' : 'de uitverkochte producten'} weg.
          </p>}
        <button type="button" disabled={soldOutCount > 0} aria-describedby={soldOutCount > 0 ? 'cart-blocked' : undefined} onClick={() => {
      // Vóór het wegnavigeren: op /afrekenen zelf telt niets mee.
      track('afrekenen-gestart', {});
      closeCart();
      navigate('/afrekenen');
    }} className={`${btnPrice} mt-4`} data-id="element-6908" data-mp="3n26yitzhfa">
          {/* Zelfde vorm als elke koopknop: de actie links, het totaal rechts (incl. verzending). */}
          <span className={btnPriceLabel} data-id="element-6909" data-mp="1o40i16r4edlf">
            <LockIcon className="h-4 w-4 shrink-0" strokeWidth={1.5} aria-hidden="true" data-id="element-6910" data-mp="1o37ml1xm3ail" />
            Veilig afrekenen
          </span>
          <span className={btnPriceAmount} data-id="element-6911" data-mp="1t0h8w8ppf627">
            <span className="sr-only" data-id="element-6912" data-mp="1xulfgqv25c2r">Totaal </span>
            {euroCents(total)}
          </span>
        </button>
        {/* Vanuit Mijn omgeving (bijv. "Opnieuw bestellen"): zelf kiezen of je weggaat. */}
        {inAccount && <button type="button" onClick={closeCart} className={`${btnSecondary} mt-2 w-full`} data-id="element-6913" data-mp="156pp03w30gef">
            Verder in Mijn omgeving
          </button>}
        <p className="mt-3 flex items-center justify-center gap-2 text-center text-[13px] text-muted" data-id="element-6914" data-mp="1f2svtn1vp37p">
          <ShieldCheckIcon className="h-4 w-4 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-6915" data-mp="1k36vagpez4u4" />
          {policyCopy.returnsShort} · {policyCopy.payments}
        </p>
      </div> : undefined;
  return <Drawer open={cartOpen} onClose={closeCart} title={count > 0 ? `Winkelmand (${count})` : 'Winkelmand'} footer={footer} data-id="element-6916" data-mp="2vfzlhn2k3l82">
      {lines.length === 0 ? <div className="flex h-full flex-col items-center justify-center text-center" data-id="element-6917" data-mp="2p127wv7nj4c8">
          <CartAdjustedNotice className="mb-8 text-left" data-id="element-6918" data-mp="2p3ewvsbtueys" />
          <p className="font-display text-2xl text-ink" data-id="element-6919" data-mp="331mnzjm6mm21">Je winkelmand is nog leeg</p>
          <p className="mt-3 max-w-xs text-[15px] leading-relaxed text-muted" data-id="element-6920" data-mp="2y0p7dbgtm02n">
            Weet je al wat je zoekt? Blader door de collectie. Twijfel je, dan wijs ik je in één minuut de juiste kant op.
          </p>
          <Link to="/webshop" onClick={closeCart} className={`${btnPrimary} mt-6`} data-id="element-6921" data-mp="37zerlaw6vwm2">
            Naar de producten
          </Link>
          <button type="button" onClick={() => {
        closeCart();
        openQuiz();
      }} aria-haspopup="dialog" className="mt-3 px-2 py-2 text-[14px] text-muted underline-offset-4 transition-colors duration-150 ease-soft hover:text-ink hover:underline" data-id="element-6922" data-mp="3lt5qzqg7i042">
            Of laat {founder.firstName} meekijken
          </button>
        </div> : <>
          <CartAdjustedNotice className="mb-4" data-id="element-6923" data-mp="i8ood3kx97vt" />
          <GiftProgress subtotal={giftSubtotal} tiers={giftTiers} next={nextGift} data-id="element-6924" data-mp="i6augy1za1pu" />

          {/* Producten — de hoofdrol */}
          {/* Een protocol staat als één bundelblok op de plek van zijn eerste product. */}
          <ul className="mt-2 divide-y divide-line" data-id="element-6925" data-mp="3qo8dprih265l">
            <AnimatePresence initial={false} data-id="element-6926" data-mp="3qmnn2cza37d0">
              {groupCart(lines).map(g => g.kind === 'bundle' ? <CartBundle key={`bundle-${g.routineId}`} routineId={g.routineId} lines={g.lines} data-id="element-6927" data-mp="3vgrqwnd8yz9v" /> : <CartLine key={g.line.product.id} line={g.line} data-id="element-6928" data-mp="3mm3qpbmj47rx" />)}
            </AnimatePresence>
          </ul>
          <CartGifts gifts={gifts} data-id="element-6929" data-mp="37pw301bwwo7w" />

          <CartRhythm data-id="element-6930" data-mp="3cofqw7a2ga2w" />

          <CartRecommendations data-id="element-6931" data-mp="3cop8b6h1p8wz" />
          <CartCode data-id="element-6932" data-mp="3cnxqcygdf9p6" />
        </>}
    </Drawer>;
}